import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Loader2, Coins, GraduationCap, Calendar, Clock, AlertTriangle, CheckCircle2, Shield, ArrowRight,
} from 'lucide-react';
import { api, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import Header from '../components/Header';

const MAX_LEAD_DAYS = 7;

/**
 * CoOpCheckout
 *
 * Student-only booking page for a Co-Op-tiered service. Enforces:
 *  - .ac.uk verified status (from /api/co-op/status)
 *  - Sufficient credit balance for the service tier
 *  - Date within 7 days (Stripe pre-auth window)
 *  - Mandatory 24h patch-test attestation + liability waiver
 */
export default function CoOpCheckout() {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [service, setService] = useState(null);
  const [coOp, setCoOp] = useState(null);
  const [tiers, setTiers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const today = useMemo(() => new Date(), []);
  const minDate = useMemo(() => today.toISOString().split('T')[0], [today]);
  const maxDate = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + MAX_LEAD_DAYS);
    return d.toISOString().split('T')[0];
  }, [today]);

  const [date, setDate] = useState(minDate);
  const [time, setTime] = useState('10:00');
  const [notes, setNotes] = useState('');
  const [patchTestConfirmed, setPatchTestConfirmed] = useState(false);
  const [waiverAccepted, setWaiverAccepted] = useState(false);

  const load = useCallback(async () => {
    // Wait for auth to resolve before deciding whether to redirect.
    // On a hard navigation to /co-op/book/:id, useAuth() starts with
    // user=null and loading=true until /api/auth/me returns.
    if (authLoading) return;
    if (!user) {
      navigate(`/login?next=/co-op/book/${serviceId}`);
      return;
    }
    setLoading(true);
    try {
      const [sRes, cRes, tRes] = await Promise.all([
        api.get(`/services/${serviceId}`),
        api.get('/co-op/status'),
        api.get('/co-op/tiers'),
      ]);
      setService(sRes.data);
      setCoOp(cRes.data);
      setTiers(tRes.data?.tiers || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load this Co-Op service.');
    } finally {
      setLoading(false);
    }
  }, [serviceId, user, authLoading, navigate]);

  useEffect(() => { load(); }, [load]);

  const tier = service?.co_op_tier ? tiers?.[service.co_op_tier] : null;
  const credits = coOp?.credits ?? 0;
  const hasEnoughCredits = tier ? credits >= tier.credits : false;

  const ready = patchTestConfirmed && waiverAccepted && hasEnoughCredits && !submitting;

  const handleBook = async () => {
    if (!ready) return;
    setSubmitting(true);
    try {
      const res = await api.post('/co-op/bookings', {
        service_id: serviceId,
        date,
        time,
        waiver_accepted: true,
        notes,
      });
      toast.success('Co-Op booking requested', {
        description: `${tier.credits} credit${tier.credits === 1 ? '' : 's'} on hold. Confirmation and provider address will unlock on approval.`,
      });
      navigate(`/booking-success/${res.data.booking_id}`, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create the Co-Op booking.');
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <div className="max-w-xl mx-auto px-4 py-16 flex items-center gap-3 text-stone-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading Co-Op checkout...
        </div>
      </div>
    );
  }

  // Gate 1: Not student-verified yet.
  if (!coOp?.is_student) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <div className="max-w-xl mx-auto px-4 py-16 text-center" data-testid="co-op-checkout-not-student">
          <div className="h-14 w-14 rounded-full bg-stone-100 flex items-center justify-center mx-auto">
            <GraduationCap className="h-6 w-6 text-stone-500" />
          </div>
          <h1 className="font-heading text-2xl mt-5 text-[#1A1A1A]">
            Verify your student status first
          </h1>
          <p className="text-stone-500 text-sm mt-2 leading-relaxed max-w-sm mx-auto">
            Co-Op bookings are open to verified UK students only. Confirm your `.ac.uk` email to unlock.
          </p>
          <Link to="/settings" className="inline-block mt-5">
            <Button className="bg-[#1A1A1A] text-white hover:bg-[#2a2a2a]">
              Verify now <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Gate 2: Service is not actually a Co-Op service.
  if (!tier) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <div className="max-w-xl mx-auto px-4 py-16 text-center">
          <div className="h-14 w-14 rounded-full bg-red-50 flex items-center justify-center mx-auto">
            <AlertTriangle className="h-6 w-6 text-red-600" />
          </div>
          <h1 className="font-heading text-2xl mt-5 text-[#1A1A1A]">Not a Co-Op service</h1>
          <p className="text-stone-500 text-sm mt-2">
            This service doesn't participate in the Student Co-Op. Use the standard checkout instead.
          </p>
          <Link to={`/services/${serviceId}`} className="inline-block mt-5">
            <Button variant="outline">Back to service</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="co-op-checkout-page">
      <Header />
      <main className="max-w-2xl mx-auto px-4 py-10">
        <div className="mb-6">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">
            Student Co-Op Checkout
          </p>
          <h1 className="font-heading text-3xl text-[#1A1A1A] mt-1">
            {service.name}
          </h1>
        </div>

        {/* Price breakdown */}
        <section
          className="rounded-xl border-2 border-[#D4AF37]/40 bg-[#FAF8F2] p-5 mb-6"
          data-testid="co-op-price-breakdown"
        >
          <h2 className="font-heading text-lg text-[#1A1A1A] mb-3">What you pay</h2>
          <div className="space-y-2.5 text-sm">
            <div className="flex justify-between items-center">
              <span className="flex items-center gap-2 text-stone-700">
                <Coins className="h-4 w-4 text-[#D4AF37]" />
                {tier.credits} credit{tier.credits === 1 ? '' : 's'}
              </span>
              <span className="font-mono text-stone-500 text-xs">frozen until completion</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-stone-700">£{tier.cash_gbp} cash</span>
              <span className="font-mono text-stone-500 text-xs">paid in person to provider</span>
            </div>
            <div className="border-t border-stone-200 pt-2.5 flex justify-between items-center">
              <span className="text-[#1A1A1A] font-medium">£7.50 Stripe hold</span>
              <span className="font-mono text-stone-500 text-xs">£2.50 fee + £5 refundable deposit</span>
            </div>
          </div>
          <p
            className={`mt-3 text-xs font-medium ${hasEnoughCredits ? 'text-emerald-700' : 'text-red-700'}`}
            data-testid="co-op-credit-status"
          >
            {hasEnoughCredits
              ? `Your balance: ${credits} credits. ${tier.credits} will be held.`
              : `Not enough credits. You have ${credits}, this service needs ${tier.credits}.`}
          </p>
        </section>

        {/* Date + time */}
        <section className="rounded-xl border border-stone-200 bg-white p-5 mb-6">
          <h2 className="font-heading text-lg text-[#1A1A1A] mb-3">Appointment</h2>
          <p className="text-xs text-stone-500 mb-3">
            Bookings can only be made up to 7 days ahead. (Stripe pre-authorisation window.)
          </p>
          <p className="text-xs text-stone-500 mb-3" data-testid="co-op-cancellation-policy">
            Cancel more than 24 hours before and the full £7.50 hold is released. Cancel within 24 hours and the provider keeps the £5 deposit. Your credits come back either way, and if the provider cancels you get everything back.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-stone-500 flex items-center gap-1 mb-1">
                <Calendar className="h-3 w-3" /> Date
              </label>
              <Input
                type="date"
                value={date}
                min={minDate}
                max={maxDate}
                onChange={(e) => setDate(e.target.value)}
                data-testid="co-op-date-input"
              />
            </div>
            <div>
              <label className="text-xs text-stone-500 flex items-center gap-1 mb-1">
                <Clock className="h-3 w-3" /> Time
              </label>
              <Input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                data-testid="co-op-time-input"
              />
            </div>
          </div>
          <Textarea
            className="mt-3"
            placeholder="Anything your provider should know? (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="co-op-notes-input"
          />
        </section>

        {/* Liability waiver */}
        <section
          className="rounded-xl border border-amber-300 bg-amber-50 p-5 mb-6"
          data-testid="co-op-waiver-section"
        >
          <h2 className="flex items-center gap-2 font-heading text-lg text-[#1A1A1A] mb-3">
            <Shield className="h-5 w-5 text-amber-600" /> Safety and liability
          </h2>
          <p className="text-sm text-stone-700 leading-relaxed">
            Zuriya is a matching venue, not a licensed salon. Your provider is a verified student, not a licensed professional. You accept the risks that come with this.
          </p>
          <div className="mt-4 space-y-3">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <Checkbox
                checked={patchTestConfirmed}
                onCheckedChange={setPatchTestConfirmed}
                data-testid="co-op-patch-test-checkbox"
              />
              <span className="text-sm text-stone-800">
                I confirm I have completed (or will complete) a 24-hour chemical patch test before any treatment involving dyes, relaxers, lash adhesive or chemical products.
              </span>
            </label>
            <label className="flex items-start gap-2.5 cursor-pointer">
              <Checkbox
                checked={waiverAccepted}
                onCheckedChange={setWaiverAccepted}
                data-testid="co-op-waiver-checkbox"
              />
              <span className="text-sm text-stone-800">
                I accept the Co-Op liability waiver and understand that Zuriya only connects me with my provider.
              </span>
            </label>
          </div>
        </section>

        <Button
          onClick={handleBook}
          disabled={!ready}
          className="w-full h-14 bg-[#1A1A1A] text-white hover:bg-[#2a2a2a] disabled:bg-stone-300 text-base rounded-none font-medium tracking-wide"
          data-testid="co-op-confirm-booking-btn"
        >
          {submitting ? (
            <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Processing...</>
          ) : (
            <><CheckCircle2 className="h-5 w-5 mr-2" /> Confirm booking + £7.50 hold</>
          )}
        </Button>

        <p className="mt-3 text-center text-[10px] font-mono tracking-[0.2em] uppercase text-stone-400">
          Hold released on completion · £2.50 non-refundable
        </p>
      </main>
    </div>
  );
}
